// src/components/admin/Users/UserDetails.tsx
// Path: /src/components/admin/Users/UserDetails.tsx
// Updated to use SocialLink with string links from SocialLinks.tsx
// Updated to add Evaluate and Verify buttons in the Actions section
import React, { useEffect, useState } from 'react';
import { withFirebase, FirebaseContextType } from '../../dev/Firebase/Firebase';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { fetchFirebasePhotos, userDocToUser } from '../../dev/utils/userHelper';
import Spinner from '../../dev/utils/Spinner/Spinner';
import { StyledImg, Thumb, ThumbInner } from '../../dev/utils/DragNDrop/Styled';
import styled from 'styled-components';
import { Button, Card, InputNumber, Typography, Modal, Select } from 'antd';
import { SocialLink, StyledLI } from '../../auth/verification/SocialLinks';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const { confirm } = Modal;
const { Option } = Select;

const RowDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

const PaddingDiv = styled.div`
  padding: 24px;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.2);
  border-radius: 8px;
  animation: fadeIn 1.5s ease-in;

  @media (max-width: 768px) {
    padding: 10px;
    .ant-card {
      font-size: 14px;
    }
    .ant-btn {
      width: 100%;
    }
  }
`;

const StyledInput = styled(InputNumber)`
  margin: 8px !important;
`;

const StyledButton = styled(Button)<{ type?: string }>`
  margin: 8px;
  background-color: ${(props) => (props.type === 'danger' ? '#ff8800' : '#3552fc')};
  color: #fff;
  &:hover {
    opacity: 0.9;
  }
`;

const StyledSelect = styled(Select)`
  margin: 8px;
  width: 200px;
`;

const StyledUL = styled.ul`
  max-width: 100%;
`;

const StyledThumb = styled(Thumb)`
  margin-right: 24px;
`;

const { Title } = Typography;

interface User {
  uid: string;
  toVerifyLinks?: string[];
  toVerifyPhotos: Array<{ filename: string; size: number }>;
  verificationLevel?: { [key: string]: number };
  isDisabled: boolean;
  phoneNumber?: string;
  name?: string;
  loc?: string;
  userRole?: string;
  email?: string;
  age?: number;
  meditationExperience?: string;
  olsExperience?: string;
  verificationStatus: string;
  submittedForVerification: boolean;
  [key: string]: any;
}

interface Photo {
  name: string;
  preview: string;
}

interface IpData {
  ip: string;
  country: string;
  countryCode: string;
  continent: string;
  asn: string;
  asName: string;
}

interface UserDetailsProps {
  firebase: FirebaseContextType;
}

const UserDetails: React.FC<UserDetailsProps> = ({ firebase }) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [ipData, setIpData] = useState<IpData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ipLoading, setIpLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  console.log('UserDetails component mounted - id:', id, 'location.state:', location.state);

  useEffect(() => {
    const initializeUser = async () => {
      const stateUser = location.state?.user;
      console.log('State user from location:', stateUser);

      if (stateUser && stateUser.uid === id) {
        console.log('Using location.state.user:', stateUser);
        setUser(stateUser);
        setLoading(false);
      } else {
        console.log('No valid location.state.user or uid mismatch, fetching from Firestore');
        await fetchUser();
      }
    };

    const fetchUser = async () => {
      if (!id) {
        console.error('No user ID provided');
        setError('Invalid user ID');
        setLoading(false);
        toast.error('Invalid user ID');
        return;
      }

      console.log('Fetching user data from Firestore for id:', id);
      try {
        const userRef = doc(firebase.firestore, 'users', id);
        const docSnap = await getDoc(userRef);
        console.log('Firestore response - exists:', docSnap.exists(), 'data:', docSnap.data());

        if (!docSnap.exists()) {
          console.warn('User document not found for id:', id);
          setError('User not found in database. Please verify the user document exists in Firestore.');
          toast.error('User not found');
          setLoading(false);
          return;
        }

        const userData = userDocToUser(docSnap);
        console.log('Parsed user data:', userData);
        setUser(userData);
      } catch (e) {
        console.error('Error fetching user from Firestore:', e);
        const errorMessage = `Failed to fetch user: ${(e as Error).message}`;
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
        console.log('Fetch user completed - loading:', false, 'error:', error, 'user:', user);
      }
    };

    initializeUser();
  }, [id, firebase, location.state]);

  useEffect(() => {
    let isMounted = true;
    console.log('UserDetails photo useEffect - user:', user?.uid);
    if (user && isMounted) {
      try {
        if (user.toVerifyPhotos && Array.isArray(user.toVerifyPhotos) && user.toVerifyPhotos.length > 0) {
          console.log('Fetching photos for user:', user.uid);
          fetchFirebasePhotos(user, firebase, (fetchedPhotos) => {
            if (isMounted) setPhotos(fetchedPhotos);
          })
            .then(() => {
              console.log('Photos fetched successfully:', photos);
            })
            .catch((e) => {
              console.error('Error fetching photos:', e);
              if (isMounted) {
                setError('Failed to fetch photos: ' + (e as Error).message);
                toast.warn('No photos available for this user');
                setPhotos([]);
              }
            });
        } else {
          console.log('No photos to fetch for user:', user.uid);
          if (isMounted) setPhotos([]);
        }
      } catch (e) {
        console.error('Error in photo fetch logic:', e);
        if (isMounted) {
          setError('Failed to process photos: ' + (e as Error).message);
          setPhotos([]);
        }
      }
    } else {
      console.log('No user data for photo fetch');
      if (isMounted) setPhotos([]);
    }
    if (isMounted) setLoading(false);

    return () => {
      isMounted = false;
    };
  }, [user, firebase]);

  useEffect(() => {
    const fetchIpData = async () => {
      if (!id) return;
      setIpLoading(true);
      try {
        const functions = getFunctions();
        const getUserIP = httpsCallable(functions, 'getUserIP');
        const result = await getUserIP({ uid: id });
        const data = result.data as IpData;
        setIpData(data);
      } catch (error) {
        console.error('Error fetching IP data:', error);
        toast.error('Failed to fetch IP data');
        setError(`Failed to fetch IP data: ${(error as Error).message}`);
      } finally {
        setIpLoading(false);
      }
    };

    if (user) fetchIpData();
  }, [user, id]);

  const confirmUserDeactivation = () => {
    confirm({
      title: 'Do you want to deactivate this user?',
      okText: 'Deactivate',
      okButtonProps: { type: 'danger' },
      async onOk() {
        try {
          const userRef = doc(firebase.firestore, 'users', id!);
          await updateDoc(userRef, { isDisabled: true });
          setUser({ ...user!, isDisabled: true });
          toast.success('User deactivated successfully');
        } catch (e) {
          console.error('Error deactivating user:', e);
          toast.error('Failed to deactivate user');
          setError('Failed to deactivate user: ' + (e as Error).message);
        }
      },
      onCancel() {},
    });
  };

  const confirmUserActivation = () => {
    confirm({
      title: 'Do you want to activate this user?',
      okText: 'Activate',
      okButtonProps: { type: 'primary' },
      async onOk() {
        try {
          const userRef = doc(firebase.firestore, 'users', id!);
          await updateDoc(userRef, { isDisabled: false });
          setUser({ ...user!, isDisabled: false });
          toast.success('User activated successfully');
        } catch (e) {
          console.error('Error activating user:', e);
          toast.error('Failed to activate user');
          setError('Failed to activate user: ' + (e as Error).message);
        }
      },
      onCancel() {},
    });
  };

  const handleVerificationStatusChange = async (status: 'Pending' | 'Verified' | 'Rejected') => {
    try {
      const userRef = doc(firebase.firestore, 'users', id!);
      const updateData = {
        verificationStatus: status,
        submittedForVerification: status === 'Pending',
        verified: status === 'Verified',
      };
      await updateDoc(userRef, updateData);
      setUser({ ...user!, verificationStatus: status, submittedForVerification: status === 'Pending', verified: status === 'Verified' });
      toast.success(`User verification status updated to ${status}`);
    } catch (error) {
      console.error(`Error updating verification status to ${status}:`, error);
      setError(`Failed to update verification status: ${(error as Error).message}`);
      toast.error('Failed to update verification status');
    }
  };

  const handleEvaluateUser = () => {
    console.log("Navigating to evaluation for user:", id);
    navigate(`/admin/evaluation/${id}`, { state: { user } });
  };

  const handleVerifyUser = () => {
    console.log("Navigating to verify users page");
    navigate('/admin/users/verify');
  };

  if (loading) {
    console.log('UserDetails rendering - loading state');
    return <Spinner />;
  }

  if (error || !user) {
    console.log('UserDetails rendering - error state:', error);
    return (
      <PaddingDiv>
        <h2>User Details Error</h2>
        <p>{error || 'No user data available'}</p>
        <p>Please check the Firestore database and try again.</p>
        <Button onClick={() => navigate('/admin/users/all', { replace: true })}>
          Back to Dashboard
        </Button>
      </PaddingDiv>
    );
  }

  console.log('UserDetails rendering - user data:', user);

  const addEstimation = (fieldName: string) => async (estimation: number) => {
    console.log('Adding estimation:', fieldName, estimation);
    const verificationLevel = { ...user.verificationLevel, [fieldName]: estimation };
    try {
      const userRef = doc(firebase.firestore, 'users', id!);
      await updateDoc(userRef, { verificationLevel });
      setUser({ ...user, verificationLevel });
      toast.success(`${fieldName} verification status updated`);
    } catch (error) {
      console.error(`Error updating ${fieldName} verification status:`, error);
      setError(`Failed to update ${fieldName} verification status: ${(error as Error).message}`);
      toast.error(`Failed to update ${fieldName} verification status`);
    }
  };

  return (
    <PaddingDiv className="user-details-container">
      <Helmet>
        <title>User Details - One Light System OLS Admin</title>
        <meta
          name="description"
          content="View and manage user details in the One Light System OLS admin panel for Sun Light Meditation education."
        />
        <meta
          name="keywords"
          content="OLS User Details, Sun Light Meditation, OLS Admin, Light Civilization"
        />
        <link rel="canonical" href={`https://www.olsme.com/admin/user/${id}`} />
      </Helmet>
      <Card
        title={`User ${user.name || user.email || id}`}
        extra={
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/users/all', { replace: true });
            }}
            aria-label="Go back to dashboard"
          >
            Back
          </a>
        }
      >
        <Title level={4}>Details</Title>
        <p><strong>Name:</strong> {user.name || 'Not provided'}</p>
        <p><strong>Email:</strong> {user.email || 'Not provided'}</p>
        <p><strong>Location:</strong> {user.loc || 'Not provided'}</p>
        <p><strong>Role:</strong> {user.userRole || 'Unknown'}</p>
        <p><strong>Age:</strong> {user.age || 'Not provided'}</p>
        <p><strong>Meditation Experience:</strong> {user.meditationExperience || 'Not provided'}</p>
        <p><strong>OLS Experience:</strong> {user.olsExperience || 'Not provided'}</p>
        <Title level={4}>IP Geolocation</Title>
        {ipLoading ? (
          <Spinner />
        ) : ipData ? (
          <div>
            <p><strong>IP Address:</strong> {ipData.ip}</p>
            <p><strong>Country:</strong> {ipData.country} ({ipData.countryCode})</p>
            <p><strong>Continent:</strong> {ipData.continent}</p>
            <p><strong>ASN:</strong> {ipData.asn} ({ipData.asName})</p>
          </div>
        ) : (
          <p>No IP data available</p>
        )}
        <Title level={4}>Verification Status</Title>
        <RowDiv>
          <p><strong>Current Status:</strong> {user.verificationStatus}</p>
          <StyledSelect
            value={user.verificationStatus}
            onChange={(value: 'Pending' | 'Verified' | 'Rejected') => handleVerificationStatusChange(value)}
            aria-label="Change verification status"
          >
            <Option value="Pending">Pending</Option>
            <Option value="Verified">Verified</Option>
            <Option value="Rejected">Rejected</Option>
          </StyledSelect>
        </RowDiv>
        <Title level={4}>Verification Links</Title>
        <RowDiv>
          <StyledUL>
            {user.toVerifyLinks && Array.isArray(user.toVerifyLinks) && user.toVerifyLinks.length > 0 ? (
              user.toVerifyLinks.map((link, i) => (
                <StyledLI key={i}>
                  <SocialLink link={link} />
                </StyledLI>
              ))
            ) : (
              <p>No verification links available</p>
            )}
          </StyledUL>
          <Estimation
            addEstimation={addEstimation('link')}
            value={user.verificationLevel?.link || 0}
          />
        </RowDiv>
        <Title level={4}>ID Photos</Title>
        <RowDiv>
          <div>
            {photos && Array.isArray(photos) && photos.length > 0 ? (
              photos.map((photo) => (
                <StyledThumb key={photo.name}>
                  <ThumbInner>
                    <StyledImg src={photo.preview} alt={`User ID photo ${photo.name}`} />
                  </ThumbInner>
                </StyledThumb>
              ))
            ) : (
              <p>No photos available</p>
            )}
          </div>
          <Estimation
            addEstimation={addEstimation('photos')}
            value={user.verificationLevel?.photos || 0}
          />
        </RowDiv>
        <Title level={4}>Phone Number</Title>
        <p>{user.phoneNumber ? `Verified ${user.phoneNumber}` : 'Not verified'}</p>
        <Title level={4}>Actions</Title>
        <RowDiv>
          {user.isDisabled ? (
            <StyledButton onClick={confirmUserActivation} type="primary">
              Activate User
            </StyledButton>
          ) : (
            <StyledButton onClick={confirmUserDeactivation} type="danger">
              Deactivate User
            </StyledButton>
          )}
          <StyledButton onClick={handleEvaluateUser} type="primary">
            Evaluate
          </StyledButton>
          <StyledButton onClick={handleVerifyUser} type="primary">
            Verify
          </StyledButton>
        </RowDiv>
      </Card>
    </PaddingDiv>
  );
};

interface EstimationProps {
  addEstimation: (estimation: number) => Promise<void>;
  value: number;
}

const Estimation: React.FC<EstimationProps> = ({ addEstimation, value }) => {
  const [estimation, setEstimation] = useState<number>(value);

  useEffect(() => {
    setEstimation(value);
  }, [value]);

  return (
    <StyledInput
      min={0}
      max={100}
      value={estimation}
      onChange={(val) => {
        if (typeof val === 'number') {
          setEstimation(val);
          addEstimation(val);
        }
      }}
      aria-label="Set verification estimation score"
    />
  );
};

export default withFirebase(UserDetails);